import { motion, useReducedMotion } from 'motion/react'
import { useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import NavTab from '@/components/NavTab'

export type NavItem = { path: string; label: string; exact: boolean }

type UnderlineRect = { left: number; width: number }

const isItemActive = (item: NavItem, pathname: string) => (item.exact ? pathname === item.path : pathname.startsWith(item.path))

// The underline's last position, kept module-level so it survives Navbar REMOUNTS. Each page
// renders its own PageLayout → Navbar, so navigating remounts this component; seeding a fresh
// mount from the previous position is what lets the underline slide from the old tab to the new
// one instead of snapping. UI-ephemeral, single header at a time — module scope is intentional.
let lastUnderline: UnderlineRect | null = null

/**
 * The header's nav tabs with one green underline sliding to the active tab. Animated via explicit
 * `x`/`width`, NOT `layoutId` — Framer's layout projection mis-projects inside this sticky header.
 */
export default function NavTabs({ items }: { items: NavItem[] }) {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef(new Map<string, HTMLAnchorElement>())
  // Seed from the cached position so this (possibly fresh) mount can animate from the old tab.
  const [underline, setUnderline] = useState<UnderlineRect | null>(lastUnderline)

  const activePath = items.find((item) => isItemActive(item, location.pathname))?.path ?? null

  // Measure the active tab and position the underline over it. Re-measures on active-tab change
  // and whenever the tab strip resizes (font load, viewport, i18n label change).
  useLayoutEffect(() => {
    const container = containerRef.current
    const activeEl = activePath ? tabRefs.current.get(activePath) : undefined
    if (!container || !activeEl) {
      setUnderline(null)
      return
    }
    const measure = () => {
      const c = container.getBoundingClientRect()
      const el = activeEl.getBoundingClientRect()
      // Offsets relative to the container, so window scroll (which shifts both boxes equally)
      // never affects the result.
      const rect = { left: el.left - c.left, width: el.width }
      lastUnderline = rect
      setUnderline(rect)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [activePath])

  return (
    <div ref={containerRef} className="relative flex items-center gap-8">
      {items.map((item) => (
        <NavTab
          key={item.path}
          path={item.path}
          label={item.label}
          isActive={isItemActive(item, location.pathname)}
          isExact={location.pathname === item.path}
          innerRef={(el) => {
            if (el) tabRefs.current.set(item.path, el)
            else tabRefs.current.delete(item.path)
          }}
        />
      ))}
      {underline && (
        <motion.div
          className="pointer-events-none absolute -bottom-2 left-0 h-0.5 rounded-full bg-brand-green"
          initial={false}
          animate={{ x: underline.left, width: underline.width }}
          // Weighty spring — a subtle overshoot rather than a snap. Disabled under reduced-motion.
          transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 170, damping: 24, mass: 1.2 }}
        />
      )}
    </div>
  )
}
